import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
// TEMPORARILY DISABLED STRIPE
// import {
//   loadStripe,
//   Stripe,
//   StripeElements,
//   StripeElementsOptions
// } from '@stripe/stripe-js';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth.service';
import { LoggerService } from '../../../services/logger.service';

interface Plan {
  plan: string;
  price: number;
  features: string[];
}

interface FeatureHighlight {
  icon: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-first-time-subscription',
  templateUrl: './first-time-subscription.component.html',
  styleUrls: ['./first-time-subscription.component.css']
})
export class FirstTimeSubscriptionComponent implements OnInit, OnDestroy {
  plan: Plan = {
    plan: "Professional",
    price: 499,
    features: [
      "Unlimited students",
      "Advanced scheduling & class management",
      "Smart double-booking prevention",
      "Studio & instructor management",
      "Square and Stripe payment processing",
      "Arthur Murray report submissions",
      "Email & phone support"
    ]
  };

  features: FeatureHighlight[] = [
    {
      icon: 'calendar-alt',
      title: 'Smart Scheduling',
      description: 'Manage classes, private lessons, and studio space'
    },
    {
      icon: 'credit-card',
      title: 'Payment Processing',
      description: 'Square terminal and Stripe online payments'
    },
    {
      icon: 'chart-line',
      title: 'Franchise Reports',
      description: 'Arthur Murray reporting automation'
    },
    {
      icon: 'users',
      title: 'Student Management',
      description: 'Track progress and manage lesson packages'
    }
  ];

  // TEMPORARILY DISABLED STRIPE
  // private stripe: Stripe | null = null;
  // elements: StripeElements | null = null;
  private stripe: any = null;
  elements: any = null;
  clientSecret: string | null = null;
  loading = false;
  errorMessage = '';
  messageTimeout: any;

  // Flag to determine whether to use payment link
  usePaymentLink = true;

  // Studio information
  studioId: string = '';
  studioName: string = '';

  // Stripe public key
  private stripePublicKey: string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private authService: AuthService
  ) {}

  async ngOnInit() {
    // Get studio information from AuthService
    if (this.authService.studios && this.authService.studios.length > 0) {
      const studio = this.authService.studios[0];

      // Get the entity ID from the studio
      this.studioId = studio.id;

      // Check if we have a field_studio_id in the studio object
      if (studio.field_studio_id && studio.field_studio_id !== '') {
        // Use field_studio_id if available
        this.studioId = studio.field_studio_id;
        LoggerService.log('Using field_studio_id value:', this.studioId);
      }

      this.studioName = studio.name || studio.label || 'Your Studio';
      LoggerService.log('Studio info from AuthService:', {
        entityId: studio.id,
        field_studio_id: studio.field_studio_id || 'not available',
        usingId: this.studioId,
        name: this.studioName
      });
    } else {
      LoggerService.error('No studios found in AuthService');
      // Fallback to query params if available
      this.route.queryParams.subscribe(params => {
        const studioIdFromParams = params['studio_id'];
        if (studioIdFromParams) {
          this.studioId = studioIdFromParams;
          this.getStudioDetails(this.studioId);
        } else {
          LoggerService.error('No studio ID available from any source');
        }
      });
    }

    // Fetch Stripe public key if needed
    if (!environment.stripePublicKey && environment.stripePublicKeyFetchUrl) {
      try {
        const response = await this.http.get<{ publicKey: string }>(
          environment.stripePublicKeyFetchUrl
        ).toPromise();

        if (response && response.publicKey) {
          this.stripePublicKey = response.publicKey;
        }
      } catch (error) {
        LoggerService.error('Error fetching Stripe public key:', error);
        this.showMessage('Failed to initialize payment system.');
      }
    } else {
      this.stripePublicKey = environment.stripePublicKey;
    }
  }

  // Get studio details based on ID
  private async getStudioDetails(studioId: string) {
    try {
      // Replace with actual API endpoint to get studio details
      const response = await this.http.get<any>(
        `/api/studio/${studioId}`
      ).toPromise();

      if (response && response.name) {
        this.studioName = response.name;
      } else {
        LoggerService.error('Failed to get studio details');
        this.studioName = 'Your Studio';
      }
    } catch (error) {
      LoggerService.error('Error fetching studio details:', error);
      this.studioName = 'Your Studio';
    }
  }

  async onSubscribe() {
    this.loading = true;

    try {
      if (this.usePaymentLink) {
        await this.createPaymentLink();
      } else {
        await this.initializeStripe();
      }
    } catch (error) {
      this.showMessage('Failed to initialize payment system.');
      LoggerService.error('Initialization error:', error);
    } finally {
      this.loading = false;
    }
  }

  private async initializeStripe() {
    // Use the dynamically fetched key or fall back to the environment variable
    const publicKey = this.stripePublicKey || environment.stripePublicKey;

    if (!publicKey) {
      this.showMessage("Stripe public key not available. Please contact support.");
      return;
    }

    // TEMPORARILY DISABLED STRIPE
    LoggerService.log('[STRIPE] Payment initialization temporarily disabled');
    this.showMessage("Payment system is temporarily disabled for maintenance.");
    return;
    
    // // Initialize Stripe
    // this.stripe = await loadStripe(publicKey);

    // if (!this.stripe) {
    //   this.showMessage("Failed to initialize Stripe.");
    //   return;
    // }

    // try {
    //   // Create a subscription intent on the server
    //   const { clientSecret } = await this.http.post<{ clientSecret: string }>(
    //     '/api/create-subscription',
    //     { priceId: 'prod_RlmhLkQVEVxpbi' }
    //   ).toPromise();

    //   this.clientSecret = clientSecret;

    //   // Initialize Elements with modern appearance
    //   const elementsOptions: StripeElementsOptions = {
    //     clientSecret,
    //     appearance: {
    //       theme: 'stripe',
    //       variables: {
    //         colorPrimary: '#4361ee',
    //         colorBackground: '#ffffff',
    //         colorText: '#2b2d42',
    //         fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
    //         borderRadius: '12px',
    //         spacingUnit: '4px',
    //         spacingGridRow: '8px',
    //       },
    //       rules: {
    //         '.Input': {
    //           border: '1px solid #e0e0e0',
    //           boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
    //         },
    //         '.Input:focus': {
    //           boxShadow: '0 0 0 2px rgba(67, 97, 238, 0.3)',
    //         },
    //         '.Label': {
    //           fontWeight: '600',
    //         }
    //       }
    //     }
    //   };

    //   this.elements = this.stripe.elements(elementsOptions);

    //   // Create and mount the Payment Element
    //   const paymentElement = this.elements.create('payment');
    //   paymentElement.mount('#payment-element');
    // } catch (error) {
    //   this.showMessage('Failed to initialize payment system.');
    //   LoggerService.error('Initialization error:', error);
    //   throw error;
    // }
  }

  private async createPaymentLink() {
    try {
      // Log the exact studio information for debugging
      LoggerService.log('Studio information being used:', {
        id: this.studioId,
        name: this.studioName,
        type: typeof this.studioId
      });

      // Create a payment link on the server, including the studio ID and name
      const response = await this.http.post<any>(
        '/api/create-payment-link',
        {
          studioId: this.studioId,
          studioName: this.studioName,
          successUrl: `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}&studio_id=${this.studioId}`,
          cancelUrl: environment.cancelRedirectUrl
        }
      ).toPromise();

      if (response && response.url) {
        // Redirect to the payment link
        window.location.href = response.url;
      } else if (response && response.error) {
        // Display server-provided error message
        this.showMessage(response.error);
        throw new Error(response.error);
      } else {
        // Handle case when response.url is null
        this.showMessage('Payment link creation failed. Please check backend configuration.');
        LoggerService.error('Invalid payment link response:', response);
        throw new Error('Invalid server response');
      }
    } catch (error: any) {
      // Handle HTTP errors
      if (error.error && error.error.message) {
        this.showMessage(error.error.message);
        LoggerService.error('Payment link error:', error.error.error_code || error.error.message);
      } else {
        this.showMessage('Failed to create payment link. Please try again later.');
        LoggerService.error('Payment link error:', error);
      }
      throw error;
    }
  }

  async handleSubmit(event: Event) {
    event.preventDefault();
    
    // TEMPORARILY DISABLED STRIPE
    this.showMessage("Payment processing is temporarily disabled.");
    return;

    // if (!this.stripe || !this.elements) {
    //   return;
    // }

    // this.setLoading(true);

    // try {
    //   const { error } = await this.stripe.confirmPayment({
    //     elements: this.elements,
    //     confirmParams: {
    //       return_url: environment.successRedirectUrl,
    //     }
    //   });

    //   if (error) {
    //     this.showMessage(error.message || 'An unexpected error occurred.');
    //   }
    // } catch (e) {
    //   this.showMessage('Payment processing failed. Please try again.');
    //   LoggerService.error('Payment error:', e);
    // } finally {
    //   this.setLoading(false);
    // }
  }

  private showMessage(messageText: string) {
    this.errorMessage = messageText;

    // Clear any existing timeout
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }

    // Auto-hide message after 8 seconds
    this.messageTimeout = setTimeout(() => {
      this.errorMessage = '';
    }, 8000);
  }

  private setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  ngOnDestroy() {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
  }
}
